import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, Eye, EyeOff, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import PasswordStrengthBar, { isPasswordAcceptable } from '@/components/PasswordStrengthBar';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function AcceptInvite() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(!token);

  useEffect(() => {
    if (!token) setInvalid(true);
  }, [token]);

  const passwordsMatch = !confirm || newPassword === confirm;
  const canSubmit = newPassword && confirm && isPasswordAcceptable(newPassword) && newPassword === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) {
      toast.error(t('settings.passwordMismatch') || 'Passwords do not match');
      return;
    }
    if (!isPasswordAcceptable(newPassword)) {
      toast.error(t('password.hint') || 'Min 8 characters, letters + numbers');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/accept-invite', null, { params: { token, new_password: newPassword } });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg?.includes('expired') || msg?.includes('Invalid')) {
        setInvalid(true);
      } else {
        toast.error(msg || t('acceptInvite.error') || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-wp-darker flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Language switcher */}
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="auth" />
        </div>

        <div className="glass-card rounded-2xl p-8 space-y-6">
          {invalid ? (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-rose-500/20 flex items-center justify-center mx-auto">
                <AlertTriangle className="h-7 w-7 text-rose-400" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t('acceptInvite.invalidTitle') || 'Link expired or invalid'}
              </h2>
              <p className="text-wp-light/50 text-sm">
                {t('acceptInvite.invalidBody') || 'Please contact your administrator to resend the invitation.'}
              </p>
            </div>
          ) : done ? (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="h-7 w-7 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t('acceptInvite.doneTitle') || 'Account activated!'}
              </h2>
              <p className="text-wp-light/50 text-sm">
                {t('acceptInvite.doneBody') || 'Redirecting to login...'}
              </p>
            </div>
          ) : (
            <>
              <div className="text-center space-y-2">
                <div className="w-14 h-14 rounded-2xl bg-wp-medium/20 flex items-center justify-center mx-auto">
                  <Lock className="h-7 w-7 text-wp-light" />
                </div>
                <h1 className="text-2xl font-bold text-white font-heading">
                  {t('acceptInvite.title') || 'Set Your Password'}
                </h1>
                <p className="text-wp-light/50 text-sm">
                  {t('acceptInvite.subtitle') || 'Create a password to activate your account'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Password field */}
                <div className="space-y-2">
                  <Label className="text-wp-light/70">{t('settings.newPassword') || 'Password'}</Label>
                  <div className="relative">
                    <Input
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="bg-wp-dark/30 border-wp-dark/50 text-white pr-10 placeholder:text-white/20"
                    />
                    <button type="button" onClick={() => setShowNew(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-wp-light/40 hover:text-wp-light transition-colors">
                      {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <PasswordStrengthBar password={newPassword} />
                </div>

                {/* Confirm password field */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-wp-light/70">{t('settings.confirmPassword') || 'Confirm password'}</Label>
                    {confirm && !passwordsMatch && (
                      <span className="text-xs text-rose-400">
                        {t('settings.passwordMismatch') || 'Passwords do not match'}
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      required
                      className={`bg-wp-dark/30 border-wp-dark/50 text-white pr-10 placeholder:text-white/20 ${
                        confirm && !passwordsMatch ? 'border-rose-500/50' : ''
                      }`}
                    />
                    <button type="button" onClick={() => setShowConfirm(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-wp-light/40 hover:text-wp-light transition-colors">
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !canSubmit}
                  className="w-full bg-wp-medium hover:bg-wp-light text-white h-11 font-medium"
                >
                  {loading
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : (t('acceptInvite.submit') || 'Activate Account')}
                </Button>
              </form>
            </>
          )}

          <Link to="/login" className="flex items-center justify-center gap-2 text-wp-light/50 hover:text-wp-light text-sm transition-colors">
            <ArrowLeft className="h-4 w-4" />
            {t('forgotPassword.backToLogin') || 'Back to login'}
          </Link>
        </div>
      </div>
    </div>
  );
}
